"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Masonry from "react-masonry-css";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Share2,
  ZoomIn,
} from "lucide-react";
import SocialShare from "@/components/SocialShare";
import type { Photo } from "@/lib/albums";

/**
 * Placeholder d'une couleur unie, encodé en URI (et non en base64) : `btoa`
 * n'existe pas côté serveur, ce qui empêcherait le rendu serveur de la grille.
 */
function blurDataURL(color: string) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"><rect width="8" height="8" fill="${color}"/></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Largeur d'affichage réelle d'une vignette, pour que next/image ne serve pas
 * une image plus grande que nécessaire. Le conteneur fait au plus 1280px, en
 * 4 colonnes avec gouttières -> environ 300px par vignette sur grand écran.
 */
const THUMB_SIZES =
  "(min-width: 1280px) 300px, (min-width: 1024px) 30vw, (min-width: 640px) 45vw, 92vw";

/** Colonnes du masonry, alignées sur THUMB_SIZES. */
const breakpointColumnsObj = {
  default: 4,
  1280: 4,
  1024: 3,
  640: 2,
  0: 1,
};

interface GalleryMasonryProps {
  photos: Photo[];
  /** Chemin de la page, pour les liens de partage (ex. "/gallery/journee-sportive"). */
  basePath: string;
}

export default function GalleryMasonry({ photos, basePath }: GalleryMasonryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const currentIndex = photos.findIndex((p) => p.id === selectedPhoto);
  const currentPhoto = currentIndex >= 0 ? photos[currentIndex] : null;

  const syncUrl = useCallback((photoId: number | null) => {
    const url = new URL(window.location.href);
    if (photoId === null) url.searchParams.delete("photo");
    else url.searchParams.set("photo", String(photoId));
    window.history.pushState({}, "", url);
  }, []);

  const openModal = useCallback(
    (photoId: number) => {
      setSelectedPhoto(photoId);
      syncUrl(photoId);
    },
    [syncUrl]
  );

  const closeModal = useCallback(() => {
    setSelectedPhoto(null);
    setShowShareMenu(false);
    syncUrl(null);
  }, [syncUrl]);

  const goTo = useCallback(
    (offset: number) => {
      if (currentIndex < 0) return;
      const nextIndex = (currentIndex + offset + photos.length) % photos.length;
      const nextId = photos[nextIndex].id;
      setSelectedPhoto(nextId);
      syncUrl(nextId);
    },
    [currentIndex, photos, syncUrl]
  );

  // Ouvre la photo demandée par l'URL (?photo=12) au chargement, pour que les
  // liens de partage pointent bien sur la bonne photo.
  // La query string n'est pas lisible au rendu serveur : l'initialiser dans le
  // useState provoquerait une divergence d'hydratation.
  useEffect(() => {
    const photoId = new URLSearchParams(window.location.search).get("photo");
    if (photoId && photos.some((p) => p.id === Number(photoId))) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedPhoto(Number(photoId));
    }
  }, [photos]);

  // Verrouille le défilement de la page et branche la navigation clavier
  // tant que la visionneuse est ouverte.
  useEffect(() => {
    if (selectedPhoto === null) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeModal();
      else if (event.key === "ArrowLeft") goTo(-1);
      else if (event.key === "ArrowRight") goTo(1);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedPhoto, closeModal, goTo]);

  const downloadPhoto = async (photoSrc: string, photoAlt: string) => {
    try {
      const response = await fetch(photoSrc);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${photoAlt.replace(/[^a-z0-9]/gi, "_").toLowerCase() || "photo_c2e"}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
    }
  };

  return (
    <>
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="my-masonry-grid"
        columnClassName="my-masonry-grid_column"
      >
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            onClick={() => openModal(photo.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openModal(photo.id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Agrandir : ${photo.alt || `photo ${index + 1}`}`}
            className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:scale-[1.02] bg-gray-50 focus:outline-none focus:ring-2 focus:ring-c2e-blue focus:ring-offset-2"
          >
            <Image
              src={photo.src}
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
              sizes={THUMB_SIZES}
              placeholder="blur"
              blurDataURL={blurDataURL(photo.blurColor)}
              // Les premières vignettes sont visibles d'emblée : on les charge
              // sans attendre pour ne pas dégrader le LCP.
              loading={index < 4 ? "eager" : "lazy"}
              className="w-full h-auto block"
            />

            {/* Overlay info */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-all duration-500">
              <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <p className="text-white text-sm font-semibold drop-shadow-lg">
                  {photo.alt}
                </p>
                <p className="text-white/80 text-xs mt-1">
                  Cliquez pour agrandir
                </p>
              </div>
            </div>

            {/* Badge loupe */}
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-300">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
                <ZoomIn className="h-4 w-4 text-white" aria-hidden="true" />
              </div>
            </div>
          </div>
        ))}
      </Masonry>

      {/* Modal Lightbox */}
      {currentPhoto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={currentPhoto.alt || "Photo de la galerie C2E"}
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md animate-fadeIn"
          onClick={closeModal}
        >
          <button
            onClick={closeModal}
            className="fixed top-4 right-4 md:top-6 md:right-6 z-[60] p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white transition-all duration-300 hover:rotate-90 shadow-xl"
            aria-label="Fermer"
          >
            <X className="h-6 w-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadPhoto(currentPhoto.src, currentPhoto.alt);
            }}
            className="fixed top-4 left-4 md:top-6 md:left-6 z-[60] p-3 rounded-full bg-c2e-blue backdrop-blur-sm hover:bg-c2e-blue-light text-white transition-all duration-300 hover:scale-110 shadow-xl group"
            aria-label="Télécharger"
            title="Télécharger la photo"
          >
            <Download className="h-6 w-6 group-hover:translate-y-0.5 transition-transform" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goTo(-1);
            }}
            className="fixed left-2 md:left-6 top-1/2 -translate-y-1/2 z-[60] p-3 md:p-4 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white transition-all duration-300 hover:scale-110 shadow-xl"
            aria-label="Photo précédente"
          >
            <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goTo(1);
            }}
            className="fixed right-2 md:right-6 top-1/2 -translate-y-1/2 z-[60] p-3 md:p-4 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white transition-all duration-300 hover:scale-110 shadow-xl"
            aria-label="Photo suivante"
          >
            <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
          </button>

          <div className="fixed inset-0 flex items-center justify-center p-4 md:p-8">
            <div
              className="relative w-full max-w-6xl animate-scaleIn"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative w-full flex items-center justify-center">
                <div className="relative rounded-xl overflow-hidden shadow-2xl">
                  <Image
                    src={currentPhoto.src}
                    alt={currentPhoto.alt}
                    width={currentPhoto.width}
                    height={currentPhoto.height}
                    sizes="(max-width: 768px) 100vw, 1200px"
                    placeholder="blur"
                    blurDataURL={blurDataURL(currentPhoto.blurColor)}
                    priority
                    className="max-h-[75vh] md:max-h-[85vh] w-auto h-auto object-contain"
                  />
                </div>
              </div>

              <div className="mt-4 md:mt-6 text-center px-4">
                {currentPhoto.alt && (
                  <p className="text-white text-lg md:text-xl font-semibold mb-2">
                    {currentPhoto.alt}
                  </p>
                )}
                <p className="text-white/60 text-sm mb-4">
                  Photo {currentIndex + 1} sur {photos.length}
                </p>

                <div className="relative inline-block">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    <Share2 className="h-5 w-5" />
                    <span className="font-medium">Partager</span>
                  </button>

                  {showShareMenu && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 animate-scaleIn">
                      <SocialShare
                        url={`${typeof window !== "undefined" ? window.location.origin : ""}${basePath}?photo=${currentPhoto.id}`}
                        title={`${currentPhoto.alt || "Photo"} - Galerie C2E Club`}
                        className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-4"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
